/**
 * StaticMeshPass.ts — instanced toon-shaded renderer for static & dynamic
 * mesh assets (dock, lighthouse, buoys, trees, glb-loaded objects).
 *
 * Holds N "MeshGroup"s. Each group owns:
 *   - one MeshAsset (vertex+index buffers on the GPU)
 *   - one storage buffer of Instance structs (model + invTrans matrices)
 *   - one bind group sharing camera & lighting with the rest of the scene
 *
 * Instances are pushed via `addInstance(group, transform)` and uploaded each
 * frame (cheap; small arrays). For purely static groups the upload is a no-op
 * after the first frame because the CPU side is unchanged.
 */

import wgsl from "../shaders/static_mesh.wgsl?raw";
import shadowWgsl from "../shaders/static_mesh_shadow.wgsl?raw";
import type { MeshAsset } from "../assets/MeshAsset";
import { MESH_VERTEX_STRIDE } from "../assets/MeshAsset";
import { createBufferFromTypedArray } from "../gpu/Buffers";
import type { ManagedRenderTarget } from "../rendering/RenderTarget";
import { REVERSE_Z_COMPARE } from "../rendering/RenderTarget";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";
import type { ShadowCaster } from "./ShadowPass";
import type { Camera } from "../Camera";
import type { LightingUniforms } from "../rendering/LightingUniforms";
import type { ShadowUniforms } from "../rendering/ShadowUniforms";

const INSTANCE_FLOATS = 32; // mat4 m + mat4 nm
const INSTANCE_BYTES = INSTANCE_FLOATS * 4;

/** A 4×4 column-major model matrix. */
export type Mat4 = Float32Array;

interface MeshGroupGpu {
  vbo: GPUBuffer;
  ibo: GPUBuffer;
  indexCount: number;
  /** GPU-side storage buffer of Instance[]. Grown lazily in powers of two. */
  storage: GPUBuffer;
  storageCapacity: number; // in instances
  /** CPU staging — flat Float32Array, 32 floats per instance. */
  cpu: Float32Array;
  bg: GPUBindGroup;
  shadowBg: GPUBindGroup | null;
  /** Live transforms (callers can mutate freely). */
  instances: Mat4[];
  /** 0 = no wind sway (buildings), 1 = full sway (trees). */
  swayAmount: number;
  /** When false, the group is skipped in the shadow pass entirely. */
  castsShadow: boolean;
  /** When true, the group is rendered into the shadow map with the
   *  hard-surface caster pipeline (depthBias / depthBiasSlopeScale set)
   *  instead of the foliage default. Use for docks, buildings, bridges,
   *  buoys — anything with large near-planar faces that suffer self-
   *  shadow acne under the low-bias foliage pipeline. */
  castsHardShadow: boolean;
  /** When false, the fragment shader bypasses shadow sampling for this
   *  group (useful for dense low-poly foliage where self-shadow noise
   *  outweighs the visual gain). */
  receivesShadow: boolean;
  /** Vertical bob amplitude in metres for floating instances (e.g. lilies
   *  on the water). 0 disables the effect. Encoded into nm[3].y. */
  floatAmp: number;
  dirty: boolean;
}

export class StaticMeshPass implements RenderPass, ShadowCaster {
  readonly name = "StaticMeshPass";

  private device: GPUDevice;
  private camera: Camera;
  private lighting: LightingUniforms;
  private pipeline: GPURenderPipeline;
  private bgl: GPUBindGroupLayout;
  private groups = new Map<string, MeshGroupGpu>();
  /** Set externally (e.g. `staticMeshPass.shadowBindGroup = shadowPass.consumerBindGroup`).
   *  When non-null the fragment shader samples the sun-perspective depth map. */
  shadowBindGroup: GPUBindGroup | null = null;
  private shadowPipeline: GPURenderPipeline | null = null;
  private hardShadowPipeline: GPURenderPipeline | null = null;
  private shadowBgl: GPUBindGroupLayout | null = null;
  private shadowBuffer: GPUBuffer | null = null;
  /** Wind uniform (vec2 dir, f32 speed, f32 time) — 16 bytes. */
  private windBuffer: GPUBuffer;
  private windCpu = new Float32Array(4);
  /** Buffers retired during dynamic growth; destroyed on pass teardown. */
  private retiredBuffers: GPUBuffer[] = [];

  constructor(
    device: GPUDevice,
    rt: ManagedRenderTarget,
    camera: Camera,
    lighting: LightingUniforms,
    shadowConsumerLayout: GPUBindGroupLayout,
    shadow?: ShadowUniforms,
  ) {
    this.device = device;
    this.camera = camera;
    this.lighting = lighting;
    if (shadow) this.shadowBuffer = shadow.buffer;

    const module = device.createShaderModule({
      label: "static_mesh.wgsl",
      code: wgsl,
    });
    this.bgl = device.createBindGroupLayout({
      label: "StaticMeshPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: "read-only-storage" },
        },
        {
          binding: 3,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: "uniform" },
        },
      ],
    });
    this.windBuffer = device.createBuffer({
      label: "StaticMeshPass.wind",
      size: 16,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.pipeline = device.createRenderPipeline({
      label: "StaticMeshPass.pipeline",
      layout: device.createPipelineLayout({
        bindGroupLayouts: [this.bgl, shadowConsumerLayout],
      }),
      vertex: {
        module,
        entryPoint: "vs_main",
        buffers: [
          {
            arrayStride: MESH_VERTEX_STRIDE,
            attributes: [
              { shaderLocation: 0, offset: 0, format: "float32x3" },
              { shaderLocation: 1, offset: 12, format: "float32x3" },
              { shaderLocation: 2, offset: 24, format: "float32x3" },
            ],
          },
        ],
      },
      fragment: {
        module,
        entryPoint: "fs_main",
        targets: [{ format: rt.current.colorFormat }],
      },
      primitive: { topology: "triangle-list", cullMode: "none" },
      depthStencil: {
        format: rt.current.depthFormat,
        depthWriteEnabled: true,
        depthCompare: REVERSE_Z_COMPARE,
      },
    });

    // --- Optional shadow caster pipeline -----------------------------------
    if (this.shadowBuffer) {
      this.shadowBgl = device.createBindGroupLayout({
        label: "StaticMeshPass.shadow.bgl",
        entries: [
          {
            binding: 0,
            visibility: GPUShaderStage.VERTEX,
            buffer: { type: "uniform" },
          },
          {
            binding: 1,
            visibility: GPUShaderStage.VERTEX,
            buffer: { type: "read-only-storage" },
          },
          {
            binding: 2,
            visibility: GPUShaderStage.VERTEX,
            buffer: { type: "uniform" },
          },
        ],
      });
      const shadowModule = device.createShaderModule({
        label: "static_mesh_shadow.wgsl",
        code: shadowWgsl,
      });
      this.shadowPipeline = device.createRenderPipeline({
        label: "StaticMeshPass.shadow.pipeline",
        layout: device.createPipelineLayout({
          bindGroupLayouts: [this.shadowBgl],
        }),
        vertex: {
          module: shadowModule,
          entryPoint: "vs_main",
          buffers: [
            {
              arrayStride: MESH_VERTEX_STRIDE,
              attributes: [
                { shaderLocation: 0, offset: 0, format: "float32x3" },
                { shaderLocation: 1, offset: 12, format: "float32x3" },
                { shaderLocation: 2, offset: 24, format: "float32x3" },
              ],
            },
          ],
        },
        // GLB foliage is double-sided (low-poly fans for needles), so
        // back-face culling here would drop ~half the leaves from the
        // shadow map and leave the ground unshaded under trees. Match
        // the main static-mesh pass which also uses cullMode: "none".
        primitive: { topology: "triangle-list", cullMode: "none" },
        depthStencil: {
          format: "depth32float",
          depthWriteEnabled: true,
          depthCompare: REVERSE_Z_COMPARE,
          // No pipeline-level bias for foliage. With aggressive negative
          // bias (as used for the boat decks), low-poly tree triangles
          // were getting pushed off the light frustum and never written
          // to the shadow map — so trees had no ground shadow at all.
          // The receiver-side `shadow.depthBias` (3e-5) is enough to
          // hide self-shadow acne on terrain at the canopy's altitude.
        },
      });
      // Hard-surface caster pipeline: same shader & layout, but with
      // negative depthBias + slope-scaled bias so large near-planar
      // faces (dock decks, embankments, bridges) don't self-shadow into
      // the fan/striped acne pattern the foliage pipeline produces.
      // Values mirror BoatRenderPass.playerShadowPipeline, which has
      // been tuned for boat decks under reverse-Z.
      this.hardShadowPipeline = device.createRenderPipeline({
        label: "StaticMeshPass.hardShadow.pipeline",
        layout: device.createPipelineLayout({
          bindGroupLayouts: [this.shadowBgl],
        }),
        vertex: {
          module: shadowModule,
          entryPoint: "vs_main",
          buffers: [
            {
              arrayStride: MESH_VERTEX_STRIDE,
              attributes: [
                { shaderLocation: 0, offset: 0, format: "float32x3" },
                { shaderLocation: 1, offset: 12, format: "float32x3" },
                { shaderLocation: 2, offset: 24, format: "float32x3" },
              ],
            },
          ],
        },
        primitive: { topology: "triangle-list", cullMode: "none" },
        depthStencil: {
          format: "depth32float",
          depthWriteEnabled: true,
          depthCompare: REVERSE_Z_COMPARE,
          depthBias: -2,
          depthBiasSlopeScale: -2.5,
          depthBiasClamp: -0.001,
        },
      });
    }
  }

  private makeShadowBg(storage: GPUBuffer, label: string): GPUBindGroup | null {
    if (!this.shadowBgl || !this.shadowBuffer) return null;
    return this.device.createBindGroup({
      label,
      layout: this.shadowBgl,
      entries: [
        { binding: 0, resource: { buffer: this.shadowBuffer } },
        { binding: 1, resource: { buffer: storage } },
        { binding: 2, resource: { buffer: this.windBuffer } },
      ],
    });
  }

  /** Register a mesh asset under a stable id. Returns the existing group if id is reused. */
  addGroup(
    id: string,
    mesh: MeshAsset,
    swayAmount = 0,
    opts: {
      castsShadow?: boolean;
      castsHardShadow?: boolean;
      receivesShadow?: boolean;
      /** Vertical bob amplitude in metres. Used by lilies / floating
       *  decoration to wave with a procedural water surface. */
      floatAmp?: number;
    } = {},
  ): void {
    if (this.groups.has(id)) return;
    const vbo = createBufferFromTypedArray(
      this.device,
      mesh.vertices,
      GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
      `StaticMeshPass[${id}].vbo`,
    );
    const ibo = createBufferFromTypedArray(
      this.device,
      mesh.indices,
      GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
      `StaticMeshPass[${id}].ibo`,
    );
    const initialCap = 8;
    const storage = this.device.createBuffer({
      label: `StaticMeshPass[${id}].instances`,
      size: initialCap * INSTANCE_BYTES,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    const cpu = new Float32Array(initialCap * INSTANCE_FLOATS);
    const bg = this.device.createBindGroup({
      label: `StaticMeshPass[${id}].bg`,
      layout: this.bgl,
      entries: [
        { binding: 0, resource: { buffer: this.camera.buffer } },
        { binding: 1, resource: { buffer: this.lighting.buffer } },
        { binding: 2, resource: { buffer: storage } },
        { binding: 3, resource: { buffer: this.windBuffer } },
      ],
    });
    this.groups.set(id, {
      vbo,
      ibo,
      indexCount: mesh.indices.length,
      storage,
      storageCapacity: initialCap,
      cpu,
      bg,
      shadowBg: this.makeShadowBg(storage, `StaticMeshPass[${id}].shadow.bg`),
      instances: [],
      swayAmount,
      castsShadow: opts.castsShadow ?? true,
      // Hard-surface shadow caster pipeline (with depthBias) is the
      // default — we have many GLB terrain objects (bridges, docks,
      // buildings, embankments…) and they all suffer self-shadow acne
      // on near-planar faces without the bias. Foliage opts out (see
      // DecorationsLibrary) because aggressive negative bias pushes
      // small/thin tree tris off the light frustum.
      castsHardShadow: opts.castsHardShadow ?? true,
      receivesShadow: opts.receivesShadow ?? true,
      floatAmp: opts.floatAmp ?? 0,
      dirty: true,
    });
  }

  /**
   * Add an instance under group `id`. Returns the index of the new instance.
   * Caller may mutate the returned Float32Array (16 floats, column-major) to
   * animate the transform; mark dirty via `markDirty(id)` afterwards.
   */
  addInstance(id: string, modelMatrix: Mat4): number {
    const g = this.groups.get(id);
    if (!g) throw new Error(`StaticMeshPass: unknown group "${id}"`);
    g.instances.push(modelMatrix);
    g.dirty = true;
    return g.instances.length - 1;
  }

  markDirty(id: string): void {
    const g = this.groups.get(id);
    if (g) g.dirty = true;
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    let drawCount = 0;
    for (const g of this.groups.values())
      if (g.instances.length > 0) drawCount++;
    if (drawCount === 0) return;

    // Update wind uniform (dir.xy, speed, time) — drives foliage sway.
    const windRad = (frame.state.windDirectionDeg * Math.PI) / 180;
    this.windCpu[0] = -Math.sin(windRad); // +X east; meteo "from" → blowing toward
    this.windCpu[1] = Math.cos(windRad);
    this.windCpu[2] = frame.state.windSpeed;
    this.windCpu[3] = frame.time;
    this.device.queue.writeBuffer(
      this.windBuffer,
      0,
      this.windCpu.buffer,
      0,
      16,
    );

    // Upload dirty groups.
    for (const g of this.groups.values()) {
      if (g.instances.length === 0) continue;
      if (g.dirty) {
        if (g.instances.length > g.storageCapacity) {
          // Grow.
          let cap = g.storageCapacity;
          while (cap < g.instances.length) cap *= 2;
          // Do not destroy immediately: previous submitted frames may still
          // reference this buffer. Retire it and free on pass destroy.
          this.retiredBuffers.push(g.storage);
          g.storage = this.device.createBuffer({
            label: `StaticMeshPass.instances[grow=${cap}]`,
            size: cap * INSTANCE_BYTES,
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
          });
          g.storageCapacity = cap;
          g.cpu = new Float32Array(cap * INSTANCE_FLOATS);
          // Rebuild bind group with new storage buffer.
          const newBg = this.device.createBindGroup({
            layout: this.bgl,
            entries: [
              { binding: 0, resource: { buffer: this.camera.buffer } },
              { binding: 1, resource: { buffer: this.lighting.buffer } },
              { binding: 2, resource: { buffer: g.storage } },
              { binding: 3, resource: { buffer: this.windBuffer } },
            ],
          });
          g.bg = newBg;
          g.shadowBg = this.makeShadowBg(
            g.storage,
            `StaticMeshPass.shadow.bg[grow]`,
          );
        }
        for (let i = 0; i < g.instances.length; i++) {
          const m = g.instances[i]!;
          const o = i * INSTANCE_FLOATS;
          // Model matrix.
          for (let k = 0; k < 16; k++) g.cpu[o + k] = m[k]!;
          // Normal matrix = inverse-transpose of upper-3×3.
          // For M = T·R·diag(sx,sy,sz) the columns of the rotation+scale block
          // are R·sx, R·sy, R·sz (length sx, sy, sz respectively). The
          // inverse-transpose is then R·diag(1/sx,1/sy,1/sz), i.e. each column
          // divided by its squared length. This handles non-uniform scale
          // correctly (e.g. tall thin trees) without breaking shading.
          for (let k = 0; k < 16; k++) g.cpu[o + 16 + k] = 0;
          const c0x = m[0]!,
            c0y = m[1]!,
            c0z = m[2]!;
          const c1x = m[4]!,
            c1y = m[5]!,
            c1z = m[6]!;
          const c2x = m[8]!,
            c2y = m[9]!,
            c2z = m[10]!;
          const inv0 = 1 / Math.max(1e-12, c0x * c0x + c0y * c0y + c0z * c0z);
          const inv1 = 1 / Math.max(1e-12, c1x * c1x + c1y * c1y + c1z * c1z);
          const inv2 = 1 / Math.max(1e-12, c2x * c2x + c2y * c2y + c2z * c2z);
          g.cpu[o + 16 + 0] = c0x * inv0;
          g.cpu[o + 16 + 1] = c0y * inv0;
          g.cpu[o + 16 + 2] = c0z * inv0;
          g.cpu[o + 16 + 4] = c1x * inv1;
          g.cpu[o + 16 + 5] = c1y * inv1;
          g.cpu[o + 16 + 6] = c1z * inv1;
          g.cpu[o + 16 + 8] = c2x * inv2;
          g.cpu[o + 16 + 9] = c2y * inv2;
          g.cpu[o + 16 + 10] = c2z * inv2;
          // nm[3].x = receivesShadow flag (1 = sample shadow map, 0 = full sun).
          g.cpu[o + 16 + 12] = g.receivesShadow ? 1.0 : 0.0;
          // nm[3].y = vertical bob amplitude (m) for floating instances.
          g.cpu[o + 16 + 13] = g.floatAmp;
          g.cpu[o + 16 + 15] = g.swayAmount;
        }
        const byteLen = g.instances.length * INSTANCE_BYTES;
        this.device.queue.writeBuffer(g.storage, 0, g.cpu.buffer, 0, byteLen);
        g.dirty = false;
      }
    }

    const pass = encoder.beginRenderPass({
      label: "StaticMeshPass",
      colorAttachments: [
        {
          view: frame.renderTarget.colorView,
          loadOp: "load",
          storeOp: "store",
        },
      ],
      depthStencilAttachment: {
        view: frame.renderTarget.depthView,
        depthLoadOp: "load",
        depthStoreOp: "store",
      },
    });
    pass.setPipeline(this.pipeline);
    if (this.shadowBindGroup !== null) {
      pass.setBindGroup(1, this.shadowBindGroup);
    }
    for (const g of this.groups.values()) {
      if (g.instances.length === 0) continue;
      pass.setBindGroup(0, g.bg);
      pass.setVertexBuffer(0, g.vbo);
      pass.setIndexBuffer(g.ibo, "uint32");
      pass.drawIndexed(g.indexCount, g.instances.length, 0, 0, 0);
    }
    pass.end();
  }

  encodeShadow(pass: GPURenderPassEncoder): void {
    if (!this.shadowPipeline) return;
    // Two passes over the groups so we don't ping-pong pipelines per
    // group: first all hard-surface casters (with depth bias), then
    // the foliage default.
    let hardBound = false;
    if (this.hardShadowPipeline) {
      for (const g of this.groups.values()) {
        if (
          g.instances.length === 0 ||
          !g.shadowBg ||
          !g.castsShadow ||
          !g.castsHardShadow
        )
          continue;
        if (!hardBound) {
          pass.setPipeline(this.hardShadowPipeline);
          hardBound = true;
        }
        pass.setBindGroup(0, g.shadowBg);
        pass.setVertexBuffer(0, g.vbo);
        pass.setIndexBuffer(g.ibo, "uint32");
        pass.drawIndexed(g.indexCount, g.instances.length, 0, 0, 0);
      }
    }
    let softBound = false;
    for (const g of this.groups.values()) {
      if (
        g.instances.length === 0 ||
        !g.shadowBg ||
        !g.castsShadow ||
        g.castsHardShadow
      )
        continue;
      if (!softBound) {
        pass.setPipeline(this.shadowPipeline);
        softBound = true;
      }
      pass.setBindGroup(0, g.shadowBg);
      pass.setVertexBuffer(0, g.vbo);
      pass.setIndexBuffer(g.ibo, "uint32");
      pass.drawIndexed(g.indexCount, g.instances.length, 0, 0, 0);
    }
  }

  destroy(): void {
    for (const g of this.groups.values()) {
      g.vbo.destroy();
      g.ibo.destroy();
      g.storage.destroy();
    }
    for (const b of this.retiredBuffers) b.destroy();
    this.retiredBuffers.length = 0;
    this.groups.clear();
  }
}
